import * as React from 'react';
import { diContainer } from 'src/dependencyInjection/diContainer';
import { DI_TYPES } from 'src/dependencyInjection/diTypes';
import { IBrowserHistory } from 'src/services/BrowserHistory/IBrowserHistoryService';
import {
  getSideItemFromType,
  TSidebarItem,
} from 'src/services/SidebarService/ISidebarService';
import { Link } from '../Shared/Link';
import { RouterLinks } from 'src/routing/routerLinks';
import { useActivityFeedBadgingContext } from '../Pages/DashboardPage/lib/ActivityFeedBadgingContext';
import { useAppointmentsRequestContext } from '../Pages/DashboardPage/lib/useAppointmentsRequestContext';
import { gql } from '@apollo/client';
import { useClearActivityFeedBadgingMutation } from './__generated__/SidebarItem.generated';
import { ActivityFeedBadgingDocument } from '../Pages/DashboardPage/lib/__generated__/ActivityFeedBadgingContext.generated';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
} from 'src/shadcn/components/ui/sidebar';
import { useMemo } from 'react';

export interface ISidebarItemProps {
  item: TSidebarItem;
  isCurrentRoute: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
}

export const SidebarItem: React.FunctionComponent<ISidebarItemProps> = ({
  item: itemType,
  isCurrentRoute,
  isDisabled,
  onClick: onClickProp,
}) => {
  const { badgeCount, onClick } = useBadgeCount(itemType.link);

  const item = useMemo(() => getSideItemFromType(itemType), [itemType]);
  const href = diContainer
    .get<IBrowserHistory>(DI_TYPES.BrowserHistoryService)
    .getUri(item.link);

  const handleClick = () => {
    onClick?.();
    onClickProp?.();
  };

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        sidebarId='main'
        asChild
        isActive={isCurrentRoute}
        tooltip={item.text}
        disabled={isDisabled}
        onClick={handleClick}
      >
        <Link href={href}>
          {item.newIcon && <item.newIcon className='size-4' />}
          <span>{item.text}</span>
          {!!badgeCount && <SidebarMenuBadge>{badgeCount}</SidebarMenuBadge>}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

function useBadgeCount(link: RouterLinks) {
  const { badgeCount: activtityFeedBadgeCount } =
    useActivityFeedBadgingContext();
  const { requestedAppointments } = useAppointmentsRequestContext();
  const { onClearBadging } = useClearActivityFeedBadging();
  let badgeCount = null;
  let onClick = undefined;
  switch (link) {
    case RouterLinks.Appointments:
      badgeCount = requestedAppointments.length;
      break;
    case RouterLinks.Notifcations:
      badgeCount = activtityFeedBadgeCount;
      if (badgeCount > 0) {
        onClick = () => {
          onClearBadging();
        };
      }
      break;
  }
  return { badgeCount, onClick };
}

function useClearActivityFeedBadging() {
  const [clearActivityFeedBadging] = useClearActivityFeedBadgingMutation({});

  return {
    onClearBadging: () => {
      clearActivityFeedBadging({
        refetchQueries: [ActivityFeedBadgingDocument],
      });
    },
  };
}

const mutation = gql`
  mutation ClearActivityFeedBadging {
    clearActivityFeedBadging
  }
`;
